/**
 * Curated icon list for the website builder icon picker.
 * Uses Lucide icon names (kebab-case) — rendered via <DynamicIcon> from lucide-react/dynamic.
 *
 * Each category key maps to an i18n key in WEBSITE_BUILDER namespace:
 *   ICON_CATEGORY_GENERAL → fr-FR.ts → "Général"
 */

export const ICON_PICKER_CATEGORIES = {
  GENERAL: [
    'heart',
    'star',
    'check',
    'check-circle',
    'award',
    'zap',
    'shield',
    'shield-check',
    'target',
    'thumbs-up',
    'smile',
    'sparkles',
    'flame',
    'gift',
    'crown',
    'gem',
    'infinity',
    'circle-check',
  ],
  HEALTH: [
    'activity',
    'heart-pulse',
    'stethoscope',
    'brain',
    'eye',
    'hand-heart',
    'baby',
    'apple',
    'leaf',
    'flower-2',
    'pill',
    'cross',
    'syringe',
    'dumbbell',
    'bike',
    'footprints',
  ],
  BUSINESS: [
    'briefcase',
    'building-2',
    'chart-bar',
    'chart-line',
    'trending-up',
    'users',
    'handshake',
    'badge-check',
    'calculator',
    'file-text',
    'clipboard-list',
    'pie-chart',
    'landmark',
    'wallet',
    'coins',
    'receipt',
  ],
  COMMUNICATION: [
    'phone',
    'mail',
    'message-circle',
    'message-square',
    'send',
    'at-sign',
    'megaphone',
    'bell',
    'share-2',
    'radio',
    'rss',
    'globe',
  ],
  TIME: ['clock', 'calendar', 'calendar-check', 'calendar-days', 'timer', 'hourglass', 'alarm-clock', 'history'],
  LOCATION: ['map-pin', 'map', 'navigation', 'compass', 'home', 'building', 'store', 'car', 'plane', 'train-front'],
  CREATIVE: [
    'palette',
    'pen-tool',
    'brush',
    'camera',
    'image',
    'film',
    'music',
    'scissors',
    'ruler',
    'puzzle',
    'lightbulb',
    'wand-2',
  ],
  EDUCATION: [
    'graduation-cap',
    'book-open',
    'notebook-pen',
    'library',
    'school',
    'presentation',
    'trophy',
    'medal',
    'microscope',
    'beaker',
  ],
  FOOD: ['utensils', 'chef-hat', 'coffee', 'wine', 'cake-slice', 'cookie', 'salad', 'soup', 'grape', 'croissant'],
  NATURE: ['sun', 'moon', 'cloud', 'mountain', 'tree-pine', 'trees', 'waves', 'droplets', 'wind', 'sprout'],
} as const;

export type IconPickerCategory = keyof typeof ICON_PICKER_CATEGORIES;

export const ICON_PICKER_CATEGORY_KEYS = Object.keys(ICON_PICKER_CATEGORIES) as IconPickerCategory[];

export const ICON_PICKER_ALL_ICONS: readonly string[] = Object.values(ICON_PICKER_CATEGORIES).flat();

/**
 * French search aliases for each icon.
 * Used by the IconPicker to let users search in French.
 */
export const ICON_PICKER_FR_ALIASES: Record<string, string> = {
  /* GENERAL */
  heart: 'coeur amour',
  star: 'etoile favori',
  check: 'valide ok coche',
  'check-circle': 'valide ok cercle',
  award: 'recompense prix trophee',
  zap: 'eclair energie rapide',
  shield: 'bouclier securite protection',
  'shield-check': 'bouclier securite valide',
  target: 'cible objectif',
  'thumbs-up': 'pouce bien bravo',
  smile: 'sourire heureux visage',
  sparkles: 'etincelles brillant magie',
  flame: 'flamme feu chaud',
  gift: 'cadeau offre present',
  crown: 'couronne roi premium',
  gem: 'diamant bijou precieux',
  infinity: 'infini illimite',
  'circle-check': 'cercle valide ok',

  /* HEALTH */
  activity: 'activite rythme cardiaque pouls',
  'heart-pulse': 'coeur pouls cardiaque sante',
  stethoscope: 'stethoscope medecin docteur',
  brain: 'cerveau mental psycho',
  eye: 'oeil vue vision regard',
  'hand-heart': 'main coeur aide soin bienveillance',
  baby: 'bebe enfant naissance maternite',
  apple: 'pomme fruit nutrition sante',
  leaf: 'feuille nature bio vegetal',
  'flower-2': 'fleur nature beaute',
  pill: 'pilule medicament pharmacie',
  cross: 'croix medical sante urgence',
  syringe: 'seringue piqure injection vaccin',
  dumbbell: 'haltere musculation sport fitness',
  bike: 'velo cyclisme sport',
  footprints: 'pas empreintes marche',

  /* BUSINESS */
  briefcase: 'mallette travail affaires bureau',
  'building-2': 'batiment immeuble entreprise bureau',
  'chart-bar': 'graphique barres statistiques',
  'chart-line': 'graphique ligne courbe evolution',
  'trending-up': 'hausse croissance tendance',
  users: 'utilisateurs equipe personnes groupe',
  handshake: 'poignee main partenariat accord',
  'badge-check': 'badge verifie certifie',
  calculator: 'calculatrice calcul comptabilite',
  'file-text': 'fichier document texte',
  'clipboard-list': 'presse-papier liste taches',
  'pie-chart': 'camembert diagramme statistiques',
  landmark: 'monument banque institution',
  wallet: 'portefeuille argent paiement',
  coins: 'pieces monnaie argent',
  receipt: 'recu facture ticket',

  /* COMMUNICATION */
  phone: 'telephone appel contact',
  mail: 'email courrier message lettre',
  'message-circle': 'message bulle discussion chat',
  'message-square': 'message carre discussion',
  send: 'envoyer envoi message',
  'at-sign': 'arobase email adresse',
  megaphone: 'megaphone annonce publicite',
  bell: 'cloche notification alerte',
  'share-2': 'partager partage reseau',
  radio: 'radio diffusion podcast',
  rss: 'flux rss abonnement',
  globe: 'globe monde international web',

  /* TIME */
  clock: 'horloge heure temps',
  calendar: 'calendrier date planning',
  'calendar-check': 'calendrier valide rendez-vous',
  'calendar-days': 'calendrier jours planning semaine',
  timer: 'minuteur chrono temps',
  hourglass: 'sablier attente temps',
  'alarm-clock': 'reveil alarme matin',
  history: 'historique passe temps',

  /* LOCATION */
  'map-pin': 'marqueur carte localisation adresse',
  map: 'carte plan geographie',
  navigation: 'navigation direction itineraire',
  compass: 'boussole direction orientation',
  home: 'maison domicile accueil',
  building: 'batiment immeuble bureau',
  store: 'magasin boutique commerce',
  car: 'voiture auto vehicule transport',
  plane: 'avion vol voyage transport',
  'train-front': 'train transport ferroviaire',

  /* CREATIVE */
  palette: 'palette couleurs peinture art',
  'pen-tool': 'plume stylo design creation',
  brush: 'pinceau peinture art dessin',
  camera: 'appareil photo photographie',
  image: 'image photo illustration',
  film: 'film video cinema',
  music: 'musique note melodie son',
  scissors: 'ciseaux couper decoupe coiffure',
  ruler: 'regle mesure dimension',
  puzzle: 'puzzle piece jeu reflexion',
  lightbulb: 'ampoule idee lumiere innovation',
  'wand-2': 'baguette magique transformation',

  /* EDUCATION */
  'graduation-cap': 'diplome chapeau etudes formation',
  'book-open': 'livre ouvert lecture',
  'notebook-pen': 'carnet stylo notes',
  library: 'bibliotheque livres savoir',
  school: 'ecole education enseignement',
  presentation: 'presentation diaporama conference',
  trophy: 'trophee coupe victoire',
  medal: 'medaille recompense prix',
  microscope: 'microscope recherche science labo',
  beaker: 'becher chimie laboratoire science',

  /* FOOD */
  utensils: 'couverts restaurant repas cuisine',
  'chef-hat': 'toque chef cuisinier',
  coffee: 'cafe tasse boisson',
  wine: 'vin verre alcool boisson',
  'cake-slice': 'gateau part patisserie dessert',
  cookie: 'biscuit gateau patisserie',
  salad: 'salade legumes sain',
  soup: 'soupe potage chaud',
  grape: 'raisin fruit vigne',
  croissant: 'croissant viennoiserie boulangerie pain',

  /* NATURE */
  sun: 'soleil jour lumiere ete',
  moon: 'lune nuit soir',
  cloud: 'nuage ciel meteo',
  mountain: 'montagne sommet randonnee',
  'tree-pine': 'sapin arbre foret conifere',
  trees: 'arbres foret nature bois',
  waves: 'vagues mer ocean eau',
  droplets: 'gouttes eau pluie',
  wind: 'vent air souffle',
  sprout: 'pousse germe plante croissance',
};
